import { useEffect } from 'react'

import { freeSignUpEventListener } from '@space-events/freesignup'
import { ProductEndpoints } from '@space-shell/common/services/product'
import { SectionsEndpoints } from '@space-shell/common/services/sections'
import UserService from '@space-shell/common/services/user/service'
import { MonitoringUtils } from '@space-shell/common/utils/monitoring'
import { queryClient } from '@space-shell/config/request'
import { RoutesPaths } from '@space-shell/core/router'
import { useMembershipStore } from '@space-shell/core/store'
import { useMutation } from '@tanstack/react-query'
import { generatePath, useNavigate, useParams } from 'react-router-dom'

import type { FreeSignUpProps } from './types'

const useFreeSignUp = () => {
  const { membershipData, membershipSlug } = useMembershipStore()
  const { lang } = useParams<{ lang: string }>()
  const navigate = useNavigate()

  const membershipId = membershipData?.id ?? ''

  const { mutate: joinProductsMutate, isLoading: isLoadingFreeSignUp } = useMutation(
    ({ productId }: FreeSignUpProps) => UserService.joinProducts(String(productId)),
    {
      onSuccess: (_, { productId, sectionId }) => {
        const goToProductPage = async () => {
          try {
            await Promise.all([
              queryClient.invalidateQueries([ProductEndpoints.purchased(membershipSlug)]),
              queryClient.invalidateQueries([ProductEndpoints.sale(membershipSlug)]),
              sectionId &&
                queryClient.invalidateQueries([
                  SectionsEndpoints.sectionsItems(membershipId, sectionId)
                ]),
              sectionId &&
                queryClient.invalidateQueries([
                  SectionsEndpoints.sectionProductDetail(membershipSlug, productId)
                ])
            ])

            const path = generatePath(`${RoutesPaths.HOME}${RoutesPaths.CONTENT_CONSUME}`, {
              membership: membershipSlug,
              productId,
              lang
            })

            navigate(path)
          } catch {
            MonitoringUtils.trace({
              message: '[FREE SIGN UP] Failed to invalidate cache and navigate to product page'
            })
          }
        }

        goToProductPage()
      }
    }
  )

  useEffect(() => {
    const freeSignUp = (event: Event) => {
      const { productId, sectionId } = (event as CustomEvent).detail
      joinProductsMutate({ productId, sectionId })
    }

    const unsubscribeFreeSignUp = freeSignUpEventListener(freeSignUp)

    return () => {
      unsubscribeFreeSignUp()
    }
  }, [joinProductsMutate])

  return { isLoadingFreeSignUp }
}

export default useFreeSignUp
